/**
 * PHASE 3: CLIENT-SIDE INTEGRATION STRENGTHENING
 * Comprehensive name validation and enforcement utilities
 */

import { User } from '@/types/auth';
import { getPersonalizationName, NamePersonalizationResult } from './namePersonalizationUtils';
import { validatePersonalizationName, sanitizeProfileData } from './nameContaminationBlocker';
import { Participant } from '@/types/category';
import { GroupSession } from '@/types/groupSession';
import { extractOrganizationIdentifiers } from './groupSessionHelpers';
import { AllowedNamesPolicy } from './nameSanitizer';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  cleanedData?: any;
  namePersonalizationResult?: NamePersonalizationResult;
}

export interface ComprehensiveNameContext {
  user: User | null;
  profileData?: any;
  groupMembers?: Participant[];
  formData?: any;
  groupSession?: GroupSession;
}

/**
 * CRITICAL: Comprehensive name validation for all sources
 */
export const validateAllNameSources = (context: ComprehensiveNameContext): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  let cleanedData: any = {};

  console.log('🔍 [COMPREHENSIVE VALIDATION] Starting validation for all name sources...');

  // 1. Validate Profile Data
  if (context.profileData) {
    const cleanProfile = sanitizeProfileData(context.profileData);
    if (cleanProfile !== context.profileData) {
      errors.push('Profile contains contaminated names that have been blocked');
      console.error('🚨 [VALIDATION] Profile contamination detected');
    }
    cleanedData.profileData = cleanProfile;

    // Validate individual profile names
    if (cleanProfile?.first_name) {
      const validation = validatePersonalizationName(cleanProfile.first_name);
      if (!validation.isClean) {
        errors.push(`Profile first name "${cleanProfile.first_name}" is contaminated`);
      }
    }

    if (cleanProfile?.preferred_name) {
      const validation = validatePersonalizationName(cleanProfile.preferred_name);
      if (!validation.isClean) {
        errors.push(`Profile preferred name "${cleanProfile.preferred_name}" is contaminated`);
      }
    }
  }

  // 2. Validate Group Members
  if (context.groupMembers && context.groupMembers.length > 0) {
    const cleanGroupMembers: Participant[] = [];
    
    context.groupMembers.forEach((member, index) => {
      const cleanMember = { ...member };
      let memberHasError = false;

      if (member.firstName) {
        const validation = validatePersonalizationName(member.firstName);
        if (!validation.isClean) {
          errors.push(`Group member ${index + 1} first name "${member.firstName}" is contaminated`);
          cleanMember.firstName = '';
          memberHasError = true;
        }
      }

      if (member.preferredName) {
        const validation = validatePersonalizationName(member.preferredName);
        if (!validation.isClean) {
          errors.push(`Group member ${index + 1} preferred name "${member.preferredName}" is contaminated`);
          cleanMember.preferredName = '';
          memberHasError = true;
        }
      }

      if (!memberHasError) {
        cleanGroupMembers.push(cleanMember);
      } else {
        warnings.push(`Group member ${index + 1} was excluded due to contaminated names`);
      }
    });

    cleanedData.groupMembers = cleanGroupMembers;
  }

  // Initialize result object
  const result: ValidationResult = {
    isValid: false, // Will be updated at the end
    errors,
    warnings,
    cleanedData,
    namePersonalizationResult: undefined
  };

  // 3. Generate group names and personalization policy
  if (context.groupSession) {
    const groupMembers = context.groupSession.participants.filter(p => 
      p.consentToName && (p.firstName || p.preferredName)
    );
    
    const groupNames = groupMembers.map(p => p.preferredName || p.firstName);
    const organizationIdentifiers = extractOrganizationIdentifiers(context.groupSession);
    
    result.namePersonalizationResult = {
      displayName: context.groupSession.group_type === 'organization' && organizationIdentifiers.length > 0 
        ? organizationIdentifiers[0] 
        : 'everyone',
      isUsingProfileName: false,
      nameUsageType: 'group'
    };
    
    // Create the policy for group sessions
    cleanedData.namePolicy = {
      usage: 'group',
      groupNames: groupNames,
      organizationIdentifiers: organizationIdentifiers.length > 0 ? organizationIdentifiers : undefined,
      organizationType: context.groupSession.group_type === 'organization' ? 'organization' : undefined
    } as AllowedNamesPolicy;
  } else {
    // 4. Generate Name Personalization Result for individual sessions
    try {
      const nameResult = getPersonalizationName(
        context.user,
        context.formData || {},
        cleanedData.profileData
      );
      
      result.namePersonalizationResult = nameResult;
      cleanedData.namePersonalization = nameResult;
      
      // Final contamination check on result
      if (nameResult.displayName && nameResult.displayName.toLowerCase().includes('vinh')) {
        errors.push('CRITICAL: Final name personalization result contains "Vinh"');
      }
      
      console.log('✅ [COMPREHENSIVE VALIDATION] Name personalization result:', nameResult);
    } catch (error) {
      errors.push(`Name personalization failed: ${error.message}`);
    }
  }

  // Update final result
  result.isValid = errors.length === 0;
  result.errors = errors;
  result.warnings = warnings;
  result.cleanedData = cleanedData;

  console.log('🔍 [COMPREHENSIVE VALIDATION] Final result:', result);
  return result;
};

/**
 * Real-time name validation for group editing interfaces
 */
export const validateGroupMemberInRealTime = (member: Participant): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (member.firstName) {
    const validation = validatePersonalizationName(member.firstName);
    if (!validation.isClean) {
      errors.push(`First name "${member.firstName}" contains forbidden content`);
    }
  }

  if (member.preferredName) {
    const validation = validatePersonalizationName(member.preferredName);
    if (!validation.isClean) {
      errors.push(`Preferred name "${member.preferredName}" contains forbidden content`);
    }
  }

  // Check for full names that need splitting
  if (member.firstName && member.firstName.includes(' ')) {
    warnings.push('First name appears to contain multiple words - consider splitting into first and last name');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Profile data consistency checker and repair utility
 */
export const validateAndRepairProfileData = (profileData: any): { 
  isConsistent: boolean; 
  repairs: string[]; 
  repairedData: any 
} => {
  const repairs: string[] = [];
  const repairedData = { ...profileData };

  // Check if full_name needs parsing
  if (repairedData.full_name && (!repairedData.first_name || !repairedData.last_name)) {
    const nameParts = repairedData.full_name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      repairedData.first_name = repairedData.first_name || nameParts[0];
      repairedData.last_name = repairedData.last_name || nameParts.slice(1).join(' ');
      repairs.push('Parsed full_name into first_name and last_name');
    }
  }

  // Validate name usage preference
  if (!repairedData.name_usage_preference) {
    repairedData.name_usage_preference = 'first';
    repairs.push('Set default name_usage_preference to "first"');
  }

  // Clean any contaminated names
  const originalData = { ...repairedData };
  const cleanedData = sanitizeProfileData(repairedData);
  if (JSON.stringify(cleanedData) !== JSON.stringify(originalData)) {
    Object.assign(repairedData, cleanedData);
    repairs.push('Removed contaminated names from profile');
  }

  return {
    isConsistent: repairs.length === 0,
    repairs,
    repairedData
  };
};

/**
 * Integration utility to ensure all prompt generators use proper personalization
 */
export const enforceNamePersonalizationInPrompts = (
  basePrompt: string,
  nameContext: ComprehensiveNameContext
): { enhancedPrompt: string; personalizationApplied: boolean } => {
  const validation = validateAllNameSources(nameContext);
  
  if (!validation.isValid) {
    console.error('🚨 [PROMPT ENFORCEMENT] Name validation failed:', validation.errors);
    return {
      enhancedPrompt: basePrompt,
      personalizationApplied: false
    };
  }

  const nameResult = validation.cleanedData?.namePersonalization as NamePersonalizationResult;
  
  if (!nameResult || !nameResult.displayName || nameResult.displayName === 'there') {
    return {
      enhancedPrompt: basePrompt,
      personalizationApplied: false
    };
  }

  // Add name personalization context to prompt
  const personalizationContext = `
CRITICAL NAME PERSONALIZATION CONTEXT:
- User's display name: ${nameResult.displayName}
- Name source: ${nameResult.isUsingProfileName ? 'profile' : 'fallback'}
- Usage type: ${nameResult.nameUsageType || 'default'}
- STRICT RULE: Only use the provided display name "${nameResult.displayName}" when addressing the user
- NEVER use any other names, especially "Vinh", "Olivia Chen", or generic placeholders
`;

  return {
    enhancedPrompt: personalizationContext + '\n\n' + basePrompt,
    personalizationApplied: true
  };
};